import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { DiagramData } from './DiagramViewer';
import { useTranslation } from 'react-i18next';
import { getImageWithFallback } from '@/utils/imageUtils';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, BookOpen, BookText, PenTool, Code, Info, MessageSquare, Send, Maximize2, Minimize2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useCredits } from '@/hooks/useCredits';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { chatWithOpenRouter } from '@/services/openRouterService.js';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';

// Interface cho các tin nhắn chat
interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  isTyping?: boolean;
  timestamp?: Date;
}

// Interface cho dữ liệu gửi đến API
interface DiagramApiData {
  title: string;
  introduction: any; // Chỉnh sửa để tránh lỗi TypeScript
  nodeName?: string;
  nodeType?: string;
  nodeContent?: string;
  nodeChildren?: Array<{ name: string, description: string }>;
  relatedConcepts?: string[];
  pathToNode?: string[];
  diagramStructure?: {
    rootNode: string;
    allNodes: Array<{ id: string, name: string, type?: string }>;
  };
  [key: string]: any;
}

interface InfoPanelProps {
  data: DiagramData;
  className?: string;
  hideTitle?: boolean;
}

// Hàm lấy tất cả các node trong diagram dưới dạng danh sách phẳng
const getAllNodesFlat = (node: DiagramData): DiagramData[] => {
  const result: DiagramData[] = [node];
  
  if (node.children && node.children.length > 0) {
    for (const child of node.children) {
      result.push(...getAllNodesFlat(child));
    }
  }
  
  return result;
};

// Hàm tìm đường dẫn từ node gốc đến một node cụ thể
const getPathToNode = (rootNode: DiagramData, targetId: string, currentPath: string[] = []): string[] | null => {
  // Thêm node hiện tại vào đường dẫn
  const newPath = [...currentPath, rootNode.name];
  
  // Nếu tìm thấy node đích
  if (rootNode.id === targetId) {
    return newPath;
  }
  
  // Kiểm tra các node con
  if (rootNode.children && rootNode.children.length > 0) {
    for (const child of rootNode.children) {
      const pathInChild = getPathToNode(child, targetId, newPath);
      if (pathInChild) {
        return pathInChild;
      }
    }
  }
  
  // Không tìm thấy trong nhánh này
  return null;
};

const InfoPanel = ({ data, className = '', hideTitle = false }: InfoPanelProps) => {
  const { t } = useTranslation(['catalog', 'common']);
  const { user, isLoading: authLoading } = useAuth();
  const { credits: userCredits, refreshCredits: refreshCreditsFromHook } = useCredits();
  
  // State cho chat
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [creditsLeft, setCreditsLeft] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  // State cho chế độ mở rộng
  const [isExpanded, setIsExpanded] = useState(false);
  // State cho chế độ chat (toàn bộ sơ đồ hoặc chỉ node hiện tại)
  const [chatMode, setChatMode] = useState<'node' | 'diagram'>('node');
  // State để quản lý timeout cho isLoading
  const [localIsLoading, setLocalIsLoading] = useState(true);
  const [forceRefreshTrigger, setForceRefreshTrigger] = useState(0);
  const [selectedNode, setSelectedNode] = useState<DiagramData | null>(null);
  const [activeTab, setActiveTab] = useState('content');
  
  // Các state cần thiết cho quản lý refresh credits
  const [forcedRefresh, setForcedRefresh] = useState<boolean | undefined>(undefined);
  const [lastRefreshTime, setLastRefreshTime] = useState<number | null>(null);
  const [isLoadingCredits, setIsLoadingCredits] = useState(false);
  const [userCreditsState, setUserCredits] = useState(0);
  const CREDITS_REFRESH_COOLDOWN = 5000; // 5 giây cooldown
  
  // Kiểm tra xem có content trong details hay không
  const hasContent = data.details?.content && data.details.content !== '';
  // Kiểm tra xem có examples trong details hay không
  const hasExamples = data.details?.examples && data.details.examples.length > 0;
  // Kiểm tra xem có references trong details hay không và tham chiếu không rỗng
  const hasReferences = data.details?.references && data.details.references.length > 0 && 
    data.details.references.some(ref => ref.title && ref.title.trim() !== '');
  // Kiểm tra xem có children hay không
  const hasChildren = data.children && data.children.length > 0;
  // Luôn hiển thị tab AI Chat
  const hasAIChat = true;
  
  // Sử dụng fallback cho các key dịch
  const getTranslation = (key: string, fallback: string) => {
    // Thêm namespace `catalog` nếu key không có namespace
    const keyWithNamespace = key.includes(':') ? key : `catalog:${key}`;
    const translated = t(keyWithNamespace);
    return translated === keyWithNamespace || translated === key ? fallback : translated;
  };
  
  // Log khi trạng thái user hoặc loading thay đổi
  useEffect(() => {
    console.log('Auth state changed:', { authLoading, isAuthenticated: !!user, userId: user?.id });
  }, [authLoading, user]);
  
  // Hàm refresh credits - có cơ chế cooldown
  const refreshCredits = useCallback(async (forceRefresh = false) => {
    // Nếu không có user đang đăng nhập thì không cần refresh
    if (!user) {
      console.log('Không có user, không refresh credits');
      return;
    }

    // Kiểm tra cooldown trừ khi force refresh
    const now = Date.now();
    if (!forceRefresh && lastRefreshTime && now - lastRefreshTime < CREDITS_REFRESH_COOLDOWN) {
      console.log('Cooldown đang active, bỏ qua refresh credits');
      return;
    }

    // Đánh dấu đang loading
    setIsLoadingCredits(true);
    
    try {
      // Gọi API để lấy thông tin credits
      const response = await fetch('/api/user/credits');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        console.log('Refresh credits thành công:', data.credits);
        setUserCredits(data.credits);
        // Reset forced refresh flag nếu là true
        if (forcedRefresh) {
          setForcedRefresh(false);
        }
      } else {
        console.error('Lỗi khi lấy credits:', data.message);
      }
    } catch (error) {
      console.error('Error refreshing credits:', error);
    } finally {
      // Cập nhật thời gian refresh cuối và tắt loading
      setLastRefreshTime(now);
      setIsLoadingCredits(false);
    }
  }, [user, lastRefreshTime, forcedRefresh]);

  // Effect để refresh credits khi cần
  useEffect(() => {
    // Chỉ refresh khi user đã load xong và không đang loading credits
    if (user && !isLoadingCredits) {
      // Nếu forcedRefresh là true hoặc undefined (lần đầu load), thì refresh credits
      if (forcedRefresh === true || forcedRefresh === undefined) {
        refreshCredits(forcedRefresh === true);
      }
    }
  }, [user, isLoadingCredits, forcedRefresh, refreshCredits]);

  // Effect cập nhật creditsLeft từ userCredits
  useEffect(() => {
    setCreditsLeft(userCredits);
  }, [userCredits]);

  // Effect để scroll chat xuống cuối khi có tin nhắn mới
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  // Xác định tab mặc định hiển thị - ưu tiên content rồi đến examples, children và cuối cùng là references
  const defaultTab = hasContent ? 'content' : 
                    hasExamples ? 'examples' : 
                    hasChildren ? 'children' : 
                    hasReferences ? 'references' : 'content';
  
  // Xử lý chuyển đổi giữa chế độ thường và mở rộng
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };
  
  // Xử lý markdown và chuyển đổi thành HTML
  const processMarkdown = (content: string): string => {
    if (!content) return '';
    
    let processedContent = content;
    
    // Xử lý tiêu đề (headers)
    processedContent = processedContent.replace(/^# (.+)$/gm, '<h1 class="text-xl font-bold my-2">$1</h1>');
    processedContent = processedContent.replace(/^## (.+)$/gm, '<h2 class="text-lg font-bold my-2">$1</h2>');
    processedContent = processedContent.replace(/^### (.+)$/gm, '<h3 class="text-md font-bold my-1">$1</h3>');
    
    // Xử lý in đậm và in nghiêng
    processedContent = processedContent.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    processedContent = processedContent.replace(/\*(.*?)\*/g, '<em>$1</em>');
    
    // Xử lý danh sách
    processedContent = processedContent.replace(/^\s*-\s+(.+)$/gm, '<li>$1</li>');
    processedContent = processedContent.replace(/(<li>.*<\/li>(\n|$))+/g, '<ul class="list-disc pl-5 my-2">$&</ul>');
    
    // Xử lý danh sách có thứ tự
    processedContent = processedContent.replace(/^\s*(\d+)\.\s+(.+)$/gm, '<li>$2</li>');
    processedContent = processedContent.replace(/(<li>.*<\/li>(\n|$))+/g, '<ul class="list-decimal pl-5 my-2">$&</ul>');
    
    // Xử lý đoạn văn
    processedContent = processedContent.replace(/^(?!<[a-z]|\s*$)(.+)$/gm, '<p>$1</p>');
    
    // Xử lý đường link
    processedContent = processedContent.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="text-blue-500 hover:underline" target="_blank">$1</a>');
    
    // Xử lý code block
    processedContent = processedContent.replace(/```([\s\S]*?)```/g, '<pre class="bg-gray-100 p-2 rounded my-2 overflow-x-auto">$1</pre>');
    
    // Xử lý inline code
    processedContent = processedContent.replace(/`(.*?)`/g, '<code class="bg-gray-100 px-1 rounded text-sm">$1</code>');
    
    // Xử lý xuống dòng
    processedContent = processedContent.replace(/\n\n/g, '<br/>');
    
    return processedContent;
  };
  
  // Hàm trừ credit đã cập nhật
  const deductCredits = async (amount = 1) => {
    // Kiểm tra xem người dùng đã đăng nhập chưa
    if (!user) {
      console.log('User chưa đăng nhập, không thể trừ credits');
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Bạn cần đăng nhập để sử dụng tính năng chat.'
      }]);
      return false;
    }
    
    // Kiểm tra xem người dùng có đủ credits không
    if (creditsLeft !== null && creditsLeft < amount) {
      console.log('Không đủ credits', { creditsLeft, amount });
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Bạn không có đủ credits để sử dụng tính năng này. Vui lòng nạp thêm credits.'
      }]);
      return false;
    }
    
    try {
      const response = await fetch('/api/user/deduct-credits', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ amount })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Force refresh credits sau khi deduct thành công
        setForcedRefresh(true);
        return true;
      } else {
        console.error('Lỗi khi trừ credits:', data.message);
        setChatMessages(prev => [...prev, {
          role: 'assistant',
          content: 'Có lỗi xảy ra khi xử lý credits. Vui lòng thử lại sau.'
        }]);
        return false;
      }
    } catch (error) {
      console.error('Error deducting credits:', error);
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Có lỗi xảy ra khi xử lý credits. Vui lòng thử lại sau.'
      }]);
      return false;
    }
  };
  
  // Xử lý khi click vào node trong chat
  const handleNodeClick = (nodeId: string) => {
    // Tìm node trong diagram data
    const foundNode = getAllNodesFlat(data).find(node => node.id === nodeId);
    if (!foundNode) {
      console.error('Node không tìm thấy:', nodeId);
      return;
    }

    // Lấy đường dẫn đến node
    const pathToNode = getPathToNode(data, nodeId);
    
    // Cập nhật selectedNode
    setSelectedNode(foundNode);
    setChatMode('node');
    
    // Reset chat khi chuyển node
    setChatMessages([]);
    setError(null);
    
    // Mở rộng panel nếu đang ở viewport nhỏ
    if (window.innerWidth < 768 && !isExpanded) {
      setIsExpanded(true);
    }
    
    // Chuyển tab sang chat
    setActiveTab('chat');
  };
  
  // Hàm xử lý việc gửi tin nhắn và tiêu thụ credits
  const handleSendMessage = useCallback(async () => {
    // Không làm gì nếu đang gửi hoặc không có input
    if (isTyping || !inputMessage.trim()) return;
    
    // Xác định tin nhắn người dùng và cập nhật UI
    const userMessage = inputMessage.trim();
    setInputMessage('');
    setIsTyping(true);
    
    // Thêm tin nhắn người dùng vào danh sách
    const newUserMessage: ChatMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    
    setChatMessages(prevMessages => [...prevMessages, newUserMessage]);
    
    // Thêm tin nhắn "đang xử lý" tạm thời
    const tempBotMessage: ChatMessage = {
      role: 'assistant',
      content: '...',
      isTyping: true,
      timestamp: new Date().toISOString()
    };
    
    setChatMessages(prevMessages => [...prevMessages, tempBotMessage]);
    
    try {
      // Kiểm tra user đăng nhập và credits
      if (!user) {
        throw new Error('Bạn cần đăng nhập để sử dụng tính năng chat');
      }
      
      if (creditsLeft === null || creditsLeft <= 0) {
        throw new Error('Bạn đã hết credits. Vui lòng nạp thêm để tiếp tục sử dụng.');
      }
      
      // Xây dựng context từ node hiện tại và mô tả của diagram
      let context = '';
      if (selectedNode) {
        context += `Node hiện tại: ${selectedNode.name}\n`;
        if (selectedNode.details?.content) {
          context += `Nội dung: ${selectedNode.details.content}\n`;
        }
      }
      
      if (data.description) {
        context += `Mô tả diagram: ${data.description}\n`;
      }
      
      // Chuẩn bị dữ liệu và gửi request
      const payload = {
        diagramId: data.id,
        message: userMessage,
        context: context,
        history: chatMessages
          .filter(msg => !msg.isTyping) // Loại bỏ tin nhắn đang loading
          .map(msg => ({
            role: msg.role,
            content: msg.content
          }))
      };
      
      // Gọi API và xử lý response
      const response = await fetch('/api/diagram-chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        throw new Error(`Lỗi API: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Trừ credits và cập nhật UI
      const deductResult = await deductCredits();
      if (!deductResult) {
        throw new Error('Không thể trừ credits. Vui lòng thử lại sau.');
      }
      
      // Cập nhật tin nhắn từ bot
      setChatMessages(prevMessages => 
        prevMessages.map(msg => 
          msg.role === 'assistant' && msg.isTyping
            ? {
                role: 'assistant',
                content: data.message,
                timestamp: new Date().toISOString()
              }
            : msg
        )
      );
    } catch (error) {
      console.error('Error in chat:', error);
      
      // Cập nhật tin nhắn lỗi
      setChatMessages(prevMessages => 
        prevMessages.map(msg => 
          msg.role === 'assistant' && msg.isTyping
            ? {
                role: 'system',
                content: `Lỗi: ${error instanceof Error ? error.message : 'Không thể xử lý yêu cầu'}`,
                timestamp: new Date().toISOString()
              }
            : msg
        )
      );
      
      // Nếu lỗi liên quan đến credits, force refresh
      if (error instanceof Error && 
          (error.message.includes('credits') || error.message.includes('đăng nhập'))) {
        setForcedRefresh(true);
      }
    } finally {
      setIsTyping(false);
    }
  }, [
    inputMessage, 
    isTyping, 
    chatMessages, 
    data.id, 
    selectedNode, 
    data.description, 
    user, 
    creditsLeft, 
    deductCredits
  ]);
  
  // Hàm scroll chat to bottom
  const scrollToBottom = () => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  // Xử lý nhấn phím Enter để gửi tin nhắn
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  
  // Render phần chat input
  const renderChatInput = () => {
    // Log detailed auth state for debugging
    console.log('Debug Auth State:', { 
      user: user ? { 
        id: user.id,
        email: user.email,
        isAuthenticated: !!user
      } : 'null',
      authLoading, 
      localIsLoading,
      credits: userCredits,
    });
    
    // TEMPORARY: Skip isLoading check to see if auth state is correct
    if (!user) {
      return (
        <div className="mt-2">
          <Alert className="mb-2">
            <AlertTitle>Debug Auth Info</AlertTitle>
            <AlertDescription className="text-xs">
              <div><strong>authLoading:</strong> {String(authLoading)}</div>
              <div><strong>localIsLoading:</strong> {String(localIsLoading)}</div>
              <div><strong>user:</strong> {user ? 'Present' : 'null'}</div>
              <div><strong>credits:</strong> {userCredits}</div>
            </AlertDescription>
          </Alert>
          <div className="mt-2 flex justify-center">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={forceRefresh}
              className="text-xs"
            >
              <Loader2 className="h-3 w-3 mr-1" />
              Đã đăng nhập nhưng vẫn thấy thông báo này? Nhấn để refresh
            </Button>
          </div>
        </div>
      );
    }
    
    // Xác định các trạng thái hiển thị dựa trên credits
    const hasEnoughCredits = creditsLeft > 0;
    const creditsLoaded = userCredits !== null;
    
    return (
      <div className="mt-2">
        {error && (
          <Alert variant="destructive" className="mb-2">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        <div className="flex flex-col gap-3">
          {/* Dropdown chọn chế độ chat */}
          <div className="flex items-center justify-between">
            <select 
              className="text-sm border rounded px-2 py-1 bg-background"
              value={chatMode}
              onChange={(e) => setChatMode(e.target.value as 'node' | 'diagram')}
            >
              <option value="node">Hỏi về node này</option>
              <option value="diagram">Hỏi về toàn sơ đồ</option>
            </select>
            <div className="text-xs text-muted-foreground">
              {creditsLoaded 
                ? `Còn ${creditsLeft} điểm AI` 
                : 'Đang tải điểm AI...'}
            </div>
          </div>
          
          <div className="flex items-end gap-2">
            <Textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Nhập câu hỏi về sơ đồ..."
              className="resize-none px-3 py-2"
              rows={2}
              disabled={!hasEnoughCredits || isTyping}
            />
            <Button 
              variant="ghost" 
              size="icon"
              className="ml-1"
              onClick={handleSendMessage}
              disabled={isTyping || !inputMessage.trim() || !hasEnoughCredits}
            >
              <Send className="h-5 w-5" />
            </Button>
          </div>
          {!hasEnoughCredits && creditsLoaded && (
            <div className="text-xs text-destructive">
              Bạn đã hết điểm AI. Vui lòng nạp thêm để tiếp tục sử dụng.
            </div>
          )}
        </div>
      </div>
    );
  };
  
  // Render tin nhắn
  const renderChatMessages = () => {
    if (chatMessages.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-20" />
          <p>Hãy đặt câu hỏi về "{data.name}" để AI giải đáp</p>
        </div>
      );
    }
    
    return chatMessages.map((message, index) => {
      if (message.isTyping) {
        return (
          <div key={`typing-${index}`} className="flex items-start gap-3 py-3">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground">
              AI
            </div>
            <div className="flex-1">
              <div className="p-3 rounded-lg bg-muted/70 animate-pulse">
                <div className="h-4 w-12 bg-muted-foreground/20 rounded mb-2"></div>
                <div className="h-4 w-24 bg-muted-foreground/20 rounded"></div>
              </div>
            </div>
          </div>
        );
      }
      
      return (
        <div 
          key={index} 
          className={`flex items-start gap-3 py-3 ${message.role === 'user' ? 'justify-end' : ''}`}
        >
          {message.role === 'assistant' && (
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold shrink-0">
              AI
            </div>
          )}
          
          <div 
            className={`max-w-[80%] px-4 py-3 rounded-lg ${
              message.role === 'user' 
                ? 'bg-primary text-primary-foreground ml-auto' 
                : 'bg-muted/70'
            }`}
          >
            {message.role === 'user' ? (
              <div className="whitespace-pre-wrap">{message.content}</div>
            ) : (
              <div className="prose prose-sm max-w-none text-foreground" dangerouslySetInnerHTML={{ __html: processMarkdown(message.content) }} />
            )}
          </div>
          
          {message.role === 'user' && (
            <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-secondary-foreground font-semibold shrink-0">
              Bạn
            </div>
          )}
        </div>
      );
    });
  };
  
  // Hàm kiểm tra session trực tiếp
  const checkDirectSession = async () => {
    try {
      // Gọi Supabase API để check session
      console.log('Manually checking supabase session...');
      
      // Import động supabase để tránh circular dependency
      const { supabase } = await import('@/lib/supabase');
      const { data, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Error checking supabase session:', error);
        return false;
      }
      
      console.log('Direct supabase session check result:', data);
      
      // Kiểm tra xem có session không
      if (data.session?.user) {
        console.log('Session user found from Supabase, but useAuth hook not working. Forcing refresh...');
        // Reload trang để khôi phục session
        window.location.reload();
        return true;
      } else {
        console.log('No session found in direct supabase check');
        return false;
      }
    } catch (error) {
      console.error('Error checking direct session:', error);
      return false;
    }
  };
  
  // Force refresh khi user là null nhưng thực tế đã đăng nhập (bug)
  const forceRefresh = async () => {
    console.log('Force refresh triggered');
    const sessionFound = await checkDirectSession();
    
    // Kích hoạt refresh credits thông qua forceRefreshTrigger
    setForceRefreshTrigger(prev => prev + 1);
    
    if (!sessionFound) {
      // Nếu không có session, có thể liên kết đến trang đăng nhập
      console.log('No session found, redirecting to login...');
      // Có thể thêm code redirect tới trang login ở đây nếu cần
    }
  };
  
  // Kiểm tra session khi component mount
  useEffect(() => {
    // Nếu không có user nhưng đã qua loading, kiểm tra session trực tiếp
    if (!user && !authLoading) {
      console.log('No user detected but loading complete, checking session directly...');
      setTimeout(() => {
        checkDirectSession();
      }, 1000); // Đợi 1 giây trước khi kiểm tra
    }
  }, [user, authLoading]);
  
  // Hàm force refresh credits
  const handleRefreshCredits = async () => {
    setForceRefreshTrigger(prev => prev + 1);
  };
  
  // Chuẩn bị context từ node hiện tại để gửi đến API
  const prepareContextForNode = async (node: DiagramData): Promise<any> => {
    // Tạo context dựa vào node được chọn
    const context = {
      diagramTitle: node.details?.title || node.name,
      introduction: typeof node.details?.content === 'object' 
        ? (node.details?.content as any)?.introduction || node.description || "Không có giới thiệu"
        : node.details?.content || node.description || "Không có giới thiệu",
      currentMode: chatMode,
    };
    
    // Thêm thông tin dựa vào chế độ chat
    if (chatMode === 'diagram') {
      // Nếu chat về toàn bộ diagram
      context.nodeChildren = node.children ? node.children.map(child => ({
        name: child.name,
        description: child.description || "Không có mô tả"
      })) : [];
      
      context.diagramStructure = {
        rootNode: node.name,
        allNodes: getAllNodesFlat(node).map(n => ({
          id: n.id,
          name: n.name,
          description: n.description || "Không có mô tả"
        }))
      };
    } else {
      // Nếu chat về node cụ thể
      context.nodeName = node.name;
      context.nodeId = node.id;
      context.nodeType = node.type || "Không xác định";
      context.nodeContent = node.details?.content || node.description || "Không có nội dung chi tiết";
      context.nodeChildren = node.children ? node.children.map(child => ({
        name: child.name,
        description: child.description || "Không có mô tả"
      })) : [];
      
      // Chuyển đổi relatedConcepts
      context.relatedConcepts = node.details?.relatedConcepts 
        ? node.details.relatedConcepts.map(concept => 
            typeof concept === 'string' ? concept : concept.name || concept.toString())
        : [];
      
      // Thêm đường dẫn đến node
      context.pathToNode = getPathToNode(node, node.id);
    }
    
    return context;
  };

  return (
    <Card className={`shadow-md border-muted InfoPanel ${isExpanded ? 'w-full md:w-3/4 max-w-4xl' : 'w-full'} transition-all duration-300 ${className}`}>
      <CardHeader className="pb-1">
        <div className="flex items-center justify-between">
          {!hideTitle && (
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl font-semibold">{data.name}</CardTitle>
                <Badge variant="outline" className="ml-2">{data.type}</Badge>
              </div>
              {data.description && (
                <CardDescription className="text-sm mt-1">{data.description}</CardDescription>
              )}
            </div>
          )}
          
          {/* Nút chuyển đổi giữa chế độ thường và mở rộng */}
          {/* <Button 
            variant="ghost" 
            size="sm" 
            className="ml-2" 
            onClick={toggleExpanded}
            title={isExpanded ? "Thu gọn" : "Mở rộng"}
          >
            {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button> */}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList className="w-full justify-start px-4 py-2 rounded-none bg-muted/50 TabsList">
            {hasContent && (
              <TabsTrigger value="content" className="text-sm">
                <BookText className="h-4 w-4 mr-1" />
                {getTranslation('diagram.details', 'Chi tiết')}
              </TabsTrigger>
            )}
            {hasExamples && (
              <TabsTrigger value="examples" className="text-sm">
                <PenTool className="h-4 w-4 mr-1" />
                {getTranslation('diagram.examples', 'Ví dụ')}
              </TabsTrigger>
            )}
            {hasReferences && (
              <TabsTrigger value="references" className="text-sm">
                <BookOpen className="h-4 w-4 mr-1" />
                {getTranslation('diagram.references', 'Tài liệu')}
              </TabsTrigger>
            )}
            {hasChildren && (
              <TabsTrigger value="children" className="text-sm">
                <Code className="h-4 w-4 mr-1" />
                {getTranslation('diagram.subCategories', 'Phân loại')}
              </TabsTrigger>
            )}
            {hasAIChat && (
              <TabsTrigger value="ai-chat" className="text-sm">
                <MessageSquare className="h-4 w-4 mr-1" />
                Hỏi AI
              </TabsTrigger>
            )}
          </TabsList>
          
          {hasContent && (
            <TabsContent value="content" className="p-4 pt-3 TabsContent">
              <div 
                className="prose prose-sm max-w-none text-foreground" 
                dangerouslySetInnerHTML={{ 
                  __html: typeof data.details?.content === 'string' 
                    ? data.details.content 
                    : (typeof data.details?.content === 'object' && data.details?.content 
                      ? (data.details.content as any).introduction || '' 
                      : '') 
                }}
              />
            </TabsContent>
          )}
          
          {hasExamples && (
            <TabsContent value="examples" className="p-4 pt-3 TabsContent">
              <h3 className="text-sm font-medium mb-2">{getTranslation('diagram.examples', 'Ví dụ')}</h3>
              <ul className="space-y-2 text-sm">
                {data.details?.examples?.map((example, index) => (
                  <li key={index} className="p-3 bg-muted/50 rounded-md">
                    {example}
                  </li>
                ))}
              </ul>
            </TabsContent>
          )}
          
          {hasReferences && (
            <TabsContent value="references" className="p-4 pt-3 TabsContent">
              <h3 className="text-sm font-medium mb-2">{getTranslation('diagram.references', 'Tài liệu')}</h3>
              <ul className="space-y-2 text-sm">
                {data.details?.references?.map((reference, index) => (
                  <li key={index} className="p-2">
                    <div className="flex items-center">
                      <BookOpen className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span className="font-medium">{reference.title}</span>
                    </div>
                    {reference.url && reference.url !== 'URL tài liệu (nếu có)' && (
                      <a 
                        href={reference.url} 
                        className="mt-1 flex items-center text-blue-500 hover:underline ml-6" 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />
                        {getTranslation('diagram.viewReference', 'Xem tài liệu')}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </TabsContent>
          )}
          
          {hasChildren && (
            <TabsContent value="children" className="p-4 pt-3 TabsContent">
              <h3 className="text-sm font-medium mb-2">{getTranslation('diagram.subCategories', 'Phân loại')}</h3>
              <ul className="grid grid-cols-1 gap-2">
                {data.children?.map((child) => (
                  <li key={child.id} className="text-sm">
                    <div className="flex items-center p-2 rounded-md bg-muted hover:bg-muted/70 transition-colors">
                      {child.image && (
                        <img 
                          src={getImageWithFallback(child.image, child.name)} 
                          alt={child.name} 
                          className="w-8 h-8 rounded-full mr-2 object-cover"
                          loading="lazy"
                        />
                      )}
                      <div className="flex-1">
                        <div className="font-medium">{child.name}</div>
                        {child.description && (
                          <div className="text-xs text-muted-foreground">
                            {child.description.length > 120 
                              ? child.description.substring(0, 120) + '...' 
                              : child.description}
                          </div>
                        )}
                      </div>
                      {/* Thêm biểu tượng để hiển thị child có chi tiết */}
                      {child.details && (
                        <Badge variant="outline" className="ml-2 text-xs">
                          <Info className="h-3 w-3 mr-1" />
                          {getTranslation('diagram.details', 'Chi tiết')}
                        </Badge>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </TabsContent>
          )}
          
          {hasAIChat && (
            <TabsContent value="ai-chat" className="p-0 TabsContent flex flex-col h-[450px]">
              {/* Thông báo lỗi nếu có */}
              {error && (
                <Alert variant="destructive" className="m-4 mb-0">
                  <AlertTitle>Lỗi</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              {/* Khu vực hiển thị tin nhắn */}
              <ScrollArea className="flex-1 p-4 mb-2">
                <div className="space-y-4">
                  {renderChatMessages()}
                  <div ref={chatEndRef} />
                </div>
              </ScrollArea>
              
              {/* Input và nút gửi */}
              <div className="px-4 pb-4">
                {renderChatInput()}
              </div>
            </TabsContent>
          )}
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default InfoPanel; 